import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { sanitizeHtml } from '../../util/sanitize';
import { Heading, Modal } from '../../components';

import css from './SectionThingsToKnow.module.css';

const ALLOWED_HTML_TAG_REGEX = /<\/?(p|strong|em|u|s|ul|ol|li|blockquote|br|hr)\b[^>]*>/i;

const renderText = (text, className) => {
  if (!text) return null;
  const isHtml = ALLOWED_HTML_TAG_REGEX.test(text);
  if (isHtml) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />;
  }
  return (
    <p className={className}>
      {richText(text, { linkify: true, longWordMinLength: 20, breakChars: '/' })}
    </p>
  );
};

const Column = ({ titleId, body, onOpen }) => (
  <div className={css.column}>
    <Heading as="h3" rootClassName={css.columnTitle}>
      <FormattedMessage id={titleId} />
    </Heading>
    <div className={css.clamp}>{renderText(body, css.body)}</div>
    <button type="button" className={css.showMore} onClick={onOpen}>
      <FormattedMessage id="ListingPage.showMore" />
    </button>
  </div>
);

/**
 * 3-column "Things to know" section: house rules, cancellation, safety.
 *
 * Props:
 *   - houseRules, cancellationPolicy, safetyInfo: optional strings
 *   - onManageDisableScrolling
 */
const SectionThingsToKnow = props => {
  const { houseRules, cancellationPolicy, safetyInfo, onManageDisableScrolling } = props;
  const [openKey, setOpenKey] = useState(null);

  const columns = [
    { key: 'houseRules', titleId: 'ListingPage.houseRulesTitle', body: houseRules },
    { key: 'cancellation', titleId: 'ListingPage.cancellationTitle', body: cancellationPolicy },
    { key: 'safety', titleId: 'ListingPage.safetyTitle', body: safetyInfo },
  ].filter(c => !!c.body);

  if (columns.length === 0) return null;

  const openColumn = columns.find(c => c.key === openKey);

  return (
    <section className={css.root}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.thingsToKnowTitle" />
      </Heading>
      <div className={css.columns}>
        {columns.map(c => (
          <Column key={c.key} titleId={c.titleId} body={c.body} onOpen={() => setOpenKey(c.key)} />
        ))}
      </div>
      <Modal
        id="ListingPage.thingsToKnow"
        isOpen={!!openColumn}
        onClose={() => setOpenKey(null)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling || (() => {})}
      >
        {openColumn ? (
          <>
            <Heading as="h2" rootClassName={css.modalTitle}>
              <FormattedMessage id={openColumn.titleId} />
            </Heading>
            <div className={css.modalBody}>{renderText(openColumn.body, css.body)}</div>
          </>
        ) : null}
      </Modal>
    </section>
  );
};

export default SectionThingsToKnow;
